import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { formatDate } from "shared/helpers/utils";
import { getItemDetails, downloadItemFile, stageBadgeClass } from "../request-orders.service";
import CloseIcon from "shared/assets/images/svg/close_form.svg";
import "../requestOrders.scss";

const DetailsDrawer = ({ show, onHide, categoryId, requestId }) => {
  const { t } = useTranslation();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    if (show && categoryId && requestId) {
      setLoading(true);
      setDetails(null);
      getItemDetails(categoryId, requestId)
        .then((data) => setDetails(data))
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [show, categoryId, requestId]);

  const handleDownload = async (field, file) => {
    setDownloading(file.id);
    try {
      await downloadItemFile(categoryId, requestId, field.id, file.id, file.fileName);
    } catch {
      toast.error(t("request-orders.downloadFailed"));
    } finally {
      setDownloading(null);
    }
  };

  const renderValue = (field) => {
    const type = (field.dataType || "").toLowerCase();

    if (type === "file") {
      if (!field.files || field.files.length === 0) return <span className="detail-empty">{t("request-orders.noValue")}</span>;
      return (
        <div className="detail-files">
          {field.files.map((file) => (
            <button
              key={file.id}
              type="button"
              className="detail-file"
              disabled={downloading === file.id}
              onClick={() => handleDownload(field, file)}
              title={t("request-orders.download")}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              <span className="detail-file-name">{file.fileName}</span>
            </button>
          ))}
        </div>
      );
    }

    const values = field.values || [];
    if (values.length === 0) return <span className="detail-empty">{t("request-orders.noValue")}</span>;

    if (type === "boolean") {
      return <span>{values[0] === "1" ? t("request-orders.yes") : t("request-orders.no")}</span>;
    }

    if (type === "date" || type === "datetime") {
      return <span>{values.map((v) => formatDate(v)).join(", ")}</span>;
    }

    // Multi-line notes keep their line breaks; multi-value fields become chips.
    if (field.isMultiple && values.length > 1) {
      return (
        <div className="detail-chips">
          {values.map((v, i) => (
            <span key={i} className="detail-chip">{v}</span>
          ))}
        </div>
      );
    }

    return <span className="detail-text">{values.join(", ")}</span>;
  };

  return (
    <SwipeableDrawer anchor="right" open={show} onClose={onHide} onOpen={() => {}}>
      <Box role="presentation" className="newRequestDrawer detailsDrawer">
        <div className="row">
          <div className="col-12">
            <div className="drawerHeader">
              <div className="info">
                <h2>
                  {t("request-orders.requestDetails")}
                  {requestId ? <span className="detail-id"> #{requestId}</span> : null}
                </h2>
              </div>
              <div className="action">
                <img src={CloseIcon} alt="close" onClick={onHide} />
              </div>
            </div>
          </div>
        </div>

        <div className="newRequestDrawer__body">
          {loading ? (
            <div className="loading-spinner">
              <div className="spinner-border text-primary" role="status" />
            </div>
          ) : !details ? (
            <p className="no-data">{t("common.no-data")}</p>
          ) : (
            <div className="drawer-fields">
              {details.fields.map((field) => (
                <div key={field.id} className="detail-row row g-3">
                  <div className="col-lg-4 detail-label">{field.name}</div>
                  <div className="col-lg-8 detail-value">{renderValue(field)}</div>
                </div>
              ))}

              <div className="detail-meta">
                <div className="detail-row row g-3">
                  <div className="col-lg-4 detail-label">{t("request-orders.stage")}</div>
                  <div className="col-lg-8 detail-value">
                    <span className={`stage-badge ${stageBadgeClass(details.stage)}`}>{details.stage}</span>
                  </div>
                </div>
                <div className="detail-row row g-3">
                  <div className="col-lg-4 detail-label">{t("request-orders.createdAt")}</div>
                  <div className="col-lg-8 detail-value">{formatDate(details.creationDate)}</div>
                </div>
                <div className="detail-row row g-3">
                  <div className="col-lg-4 detail-label">{t("request-orders.updatedAt")}</div>
                  <div className="col-lg-8 detail-value">{formatDate(details.updatedDate)}</div>
                </div>
                {details.movedDate && (
                  <div className="detail-row row g-3">
                    <div className="col-lg-4 detail-label">{t("request-orders.movedAt")}</div>
                    <div className="col-lg-8 detail-value">{formatDate(details.movedDate)}</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="newRequestDrawer__footer">
          <button className="btn-border-blue" onClick={onHide}>
            {t("request-orders.close")}
          </button>
        </div>
      </Box>
    </SwipeableDrawer>
  );
};

export default DetailsDrawer;
